// @flow
import React, { useEffect, useMemo } from 'react';
import { AnyAction } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import _trim from 'lodash/trim';
import _filter from 'lodash/filter';
import gql from 'graphql-tag';
import { supportMocking } from 'metaup/dev/supportMocking';

import SearchDeviceWidgetView from './views/SearchDeviceWidgetView';
import { redirect } from '../core/data/router.redux';
import { apolloQuery } from '../core/data/apolloClient';
import type { Device } from '../library/model/Device.model';

type SearchDeviceState = {
  searchTerm: string,
  expanded: boolean,
  selectedIndex: number,
  allDevices: ?Array<Device>, // null = ещё не загружено; [] = загружено пусто
  loadFailed: boolean,
}

const initialState: SearchDeviceState = {
  searchTerm: '',
  expanded: false,
  selectedIndex: 0,
  allDevices: null,
  loadFailed: false,
};

const ACTION_UPDATE_STATE = 'frontdesk/searchDevice/ACTION_UPDATE_STATE';

function setState(diff) {
  return {
    type: ACTION_UPDATE_STATE,
    diff,
  };
}

export function searchDeviceReducer(
  state: SearchDeviceState = initialState,
  action: AnyAction
) {
  switch (action.type) {
    case ACTION_UPDATE_STATE:
      return {
        ...state,
        ...action.diff,
      };
    default:
      return state;
  }
}

// Один запрос на весь список — после этого поиск полностью клиентский, без сети.
const listDevicesQuery = gql`
  query ($filter: DevicesFilter) {
    listDevices(filter: $filter) {
      id
      name
      preview {
        id
        fit(width: 70, height: 76) { width height url }
      }
      brand {
        id
        name
      }
    }
  }
`;

async function loadAllDevices() {
  try {
    const data = await apolloQuery(
      listDevicesQuery,
      { filter: { _sort: 'name' } }
    );
    return setState({ allDevices: data || [], loadFailed: false });
  } catch (err) {
    console.error('Device preload failed:', err);
    return setState({ allDevices: [], loadFailed: true });
  }
}

/**
 * Скорим результаты по релевантности.
 * Чем меньше score — тем релевантнее (как в Vim/grep — ближе к началу = лучше).
 *
 * Логика:
 * - Точное совпадение полного имени "brand name" → 0
 * - Совпадение начала имени устройства → 1
 * - Все термы найдены в имени устройства → 2
 * - Все термы найдены в имени устройства ИЛИ бренда → 3
 * - Иначе — отбрасываем
 */
function scoreAndFilter(devices: Array<any>, searchTerm: string): Array<Device> {
  const terms = _filter(searchTerm.toLowerCase().split(/\s+/));
  if (!terms.length) return [];

  const fullQuery = terms.join(' ');

  const scored = [];
  devices.forEach((device) => {
    if (!device.brand) return;

    const deviceName = (device.name || '').toLowerCase();
    const brandName = (device.brand.name || '').toLowerCase();
    const fullName = `${brandName} ${deviceName}`;

    if (fullName === fullQuery || deviceName === fullQuery) {
      scored.push({ device, score: 0 });
      return;
    }

    if (deviceName.startsWith(fullQuery)) {
      scored.push({ device, score: 1 });
      return;
    }

    const allInDevice = terms.every(t => deviceName.indexOf(t) !== -1);
    if (allInDevice) {
      scored.push({ device, score: 2 });
      return;
    }

    const allInEither = terms.every(t => (
      deviceName.indexOf(t) !== -1 || brandName.indexOf(t) !== -1
    ));
    if (allInEither) {
      scored.push({ device, score: 3 });
    }
  });

  scored.sort((a, b) => {
    if (a.score !== b.score) return a.score - b.score;
    const aFull = `${a.device.brand.name} ${a.device.name}`.toLowerCase();
    const bFull = `${b.device.brand.name} ${b.device.name}`.toLowerCase();
    return aFull.localeCompare(bFull);
  });

  return scored.slice(0, 30).map(s => s.device);
}

type Props = {};

function SearchDeviceWidget(props: Props) {
  const dispatch = useDispatch();
  const {
    searchTerm,
    expanded,
    selectedIndex,
    allDevices,
    loadFailed,
  } = useSelector(({ frontdesk }) => frontdesk.searchDevice);

  // Грузим весь список один раз на маунт. Если прошлый вызов зафейлился — ретраим.
  useEffect(() => {
    if (!allDevices || loadFailed) {
      dispatch(loadAllDevices());
    }
  }, []);

  // Фильтрация полностью в памяти. Никаких сетевых запросов на ввод.
  const results = useMemo(() => {
    const cleanTerm = _trim(searchTerm);
    if (!cleanTerm || !allDevices) return [];
    return scoreAndFilter(allDevices, cleanTerm);
  }, [searchTerm, allDevices]);

  // Спиннер показываем только если юзер уже ввёл, а список ещё не подъехал
  const loading = !allDevices && _trim(searchTerm).length > 0;

  const effectiveSelectedIndex = selectedIndex < results.length ? selectedIndex : 0;
  const selectedDevice = results[effectiveSelectedIndex] || null;

  return (
    <SearchDeviceWidgetView
      searchTerm={searchTerm}
      devices={results}
      expanded={expanded && (results.length > 0 || loading || !!_trim(searchTerm))}
      loading={loading}
      selectedDeviceId={selectedDevice ? selectedDevice.id : null}
      onSetExpanded={value => dispatch(setState({ expanded: value }))}
      onSelectResult={() => dispatch(setState({ searchTerm: '', expanded: false, selectedIndex: 0 }))}
      onType={value => dispatch(setState({
        searchTerm: value,
        selectedIndex: 0,
        expanded: true,
      }))}
      onMove={(delta) => {
        if (results.length === 0) return;
        dispatch(setState({
          selectedIndex: (results.length + effectiveSelectedIndex + delta) % results.length,
        }));
      }}
      onEnter={() => {
        if (selectedDevice) {
          dispatch([
            setState({ searchTerm: '', expanded: false }),
            redirect(`/frontdesk/${selectedDevice.brand.id}/${selectedDevice.id}/`),
          ]);
        }
      }}
    />
  );
}

export default supportMocking(SearchDeviceWidget, SearchDeviceWidgetView);
